import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE from "./apiBase";

/* ================= DEFAULT FAQS ================= */
const DEFAULT_FAQS = [
  {
    id: 1,
    question: "How long does it take to process my request?",
    answer: "Under the DPDP Act 2023, we are required to respond to your request within 30 days. We strive to complete most requests within 15 days."
  },
  {
    id: 2,
    question: "How will I receive my data?",
    answer: "You will receive your data via secure email in a machine-readable format (typically JSON or CSV). For large datasets, we may provide a secure download link."
  },
  {
    id: 3,
    question: "Can I request deletion of all my data?",
    answer: "Yes, you can request erasure of your data. However, we may need to retain certain data for legal or regulatory compliance purposes."
  },
  {
    id: 4,
    question: "Is there a fee for making requests?",
    answer: "No, there is no fee for making Data Principal Rights requests under the DPDP Act 2023."
  },
  {
    id: 5,
    question: "What information do I need to provide?",
    answer: "You need to provide sufficient information to verify your identity and specify the nature of your request. This typically includes your full name, email address, and relevant account details."
  },
  {
    id: 6,
    question: "Can someone else make a request on my behalf?",
    answer: "Yes, you can authorize a representative to make requests on your behalf. You must provide written authorization and proof of identity for both you and your representative."
  }
];

export const mergeFaqsWithDefaults = (tenantFaqs = []) => {
  const normalizedFaqs = Array.isArray(tenantFaqs) ? tenantFaqs.filter(faq => faq && (faq.question || faq.answer)) : [];
  const merged = [...normalizedFaqs];

  const keyFor = (faq) => {
    const question = String(faq?.question || '').trim().toLowerCase();
    const answer = String(faq?.answer || '').trim().toLowerCase();
    return `${question}::${answer}`;
  };

  const seen = new Set(normalizedFaqs.map(keyFor));

  DEFAULT_FAQS.forEach((defaultFaq) => {
    const defaultKey = keyFor(defaultFaq);
    if (!seen.has(defaultKey)) {
      seen.add(defaultKey);
      merged.push(defaultFaq);
    }
  });

  return merged;
};

const normalizeQuestion = (question) => String(question || '').trim().toLowerCase();

const ManagerFAQManagement = ({ tenantId, token, onBack }) => {
  const [faqs, setFaqs] = useState([]);
  const [storedFaqs, setStoredFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(-1);
  const [newFaqs, setNewFaqs] = useState([{ question: "", answer: "" }]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ question: "", answer: "" });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const pageCount = Math.max(1, Math.ceil(faqs.length / itemsPerPage));

  const currentFaqs = faqs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // CSV Upload State
  const [uploadStatus, setUploadStatus] = useState(null);
  const [csvErrors, setCsvErrors] = useState([]);
  const fileInputRef = useRef(null);

  /* ================= LOAD FAQS ================= */
  const fetchFaqs = useCallback(async () => {
    if (!tenantId || !token) return;
    
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/manager/faq`, {
        params: { tenant_id: tenantId, include_inactive: true },
        headers: { Authorization: `Bearer ${token}` }
      });

      const records = Array.isArray(res.data) ? res.data : [];
      setStoredFaqs(records);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setFaqs(mergeFaqsWithDefaults(res.data));
      } else {
        setFaqs(DEFAULT_FAQS);
      }
    } catch (err) {
      console.error("Failed to load FAQs, using defaults", err);
      setStoredFaqs([]);
      setFaqs(DEFAULT_FAQS);
    } finally {
      setLoading(false);
    }
  }, [tenantId, token]); // Only depend on tenantId and token

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  /* ================= ACCORDION ================= */
  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  /* ================= BULK ADD ================= */
  const addRow = () => setNewFaqs([...newFaqs, { question: "", answer: "" }]);

  const updateRow = (i, field, value) => {
    const copy = [...newFaqs];
    copy[i][field] = value;
    setNewFaqs(copy);
  };

  const getUniqueFaqs = (candidateFaqs) => {
    const existingQuestions = new Set(faqs.map(faq => normalizeQuestion(faq.question)));
    const seenQuestions = new Set();
    const uniqueFaqs = [];
    const duplicateQuestions = [];

    candidateFaqs.forEach((faq) => {
      const normalizedQuestion = normalizeQuestion(faq.question);
      if (existingQuestions.has(normalizedQuestion) || seenQuestions.has(normalizedQuestion)) {
        duplicateQuestions.push(faq.question.trim());
        return;
      }
      seenQuestions.add(normalizedQuestion);
      uniqueFaqs.push(faq);
    });

    return { uniqueFaqs, duplicateQuestions };
  };

  const showDuplicateAlert = async (duplicateQuestions, createdCount) => {
    if (duplicateQuestions.length === 0) return;
    await Swal.fire({
      icon: "warning",
      title: "Duplicate questions skipped",
      text: `${createdCount > 0 ? `${createdCount} FAQ(s) uploaded. ` : ''}Skipped: ${duplicateQuestions.join(', ')}`,
      customClass: { popup: "swal-popup-custom" },
    });
  };

  const submitBulk = async () => {
    if (newFaqs.some(f => !f.question.trim() || !f.answer.trim())) {
      await Swal.fire({
        icon: "warning",
        title: "Missing FAQ content",
        text: "Please fill all questions and answers.",
        customClass: { popup: "swal-popup-custom" },
      });
      return;
    }

    const { uniqueFaqs, duplicateQuestions } = getUniqueFaqs(newFaqs);
    if (uniqueFaqs.length === 0) {
      await showDuplicateAlert(duplicateQuestions, 0);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE}/manager/faq/bulk`,
        { tenant_id: tenantId, faqs: uniqueFaqs },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewFaqs([{ question: "", answer: "" }]);
      await fetchFaqs();
      const serverDuplicates = response.data?.duplicate_questions || [];
      await showDuplicateAlert(
        [...duplicateQuestions, ...serverDuplicates],
        response.data?.created ?? uniqueFaqs.length
      );
      if (duplicateQuestions.length > 0 || serverDuplicates.length > 0) return;
      await Swal.fire({
        icon: "success",
        title: "FAQs Saved",
        text: "Bulk FAQs were saved successfully.",
        customClass: { popup: "swal-popup-custom" },
      });
    } catch (err) {
      console.error("Failed to save FAQs:", err);
      await Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: "Failed to save FAQs. Please try again.",
        customClass: { popup: "swal-popup-custom" },
      });
    } finally {
      setLoading(false);
    }
  };

  /* ================= CSV UPLOAD ================= */
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file is empty or has no data rows');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    
    const questionIndex = headers.findIndex(h => h.includes('question'));
    const answerIndex = headers.findIndex(h => h.includes('answer'));

    if (questionIndex === -1 || answerIndex === -1) {
      throw new Error('CSV must have "Question" and "Answer" columns');
    }

    const faqs = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      // Simple CSV parsing (handle quoted values)
      const line = lines[i];
      const values = [];
      let currentValue = '';
      let insideQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim());

      const question = values[questionIndex]?.replace(/^"|"$/g, '').trim();
      const answer = values[answerIndex]?.replace(/^"|"$/g, '').trim();

      if (!question || !answer) {
        errors.push(`Row ${i + 1}: Missing question or answer`);
        continue;
      }

      faqs.push({ question, answer });
    }

    return { faqs, errors };
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setUploadStatus('error');
      setCsvErrors(['Please upload a CSV file']);
      return;
    }

    try {
      setLoading(true);
      setUploadStatus(null);
      setCsvErrors([]);

      const text = await file.text();
      const { faqs: parsedFaqs, errors } = parseCSV(text);

      if (errors.length > 0) {
        setCsvErrors(errors);
      }

      if (parsedFaqs.length === 0) {
        setUploadStatus('error');
        setCsvErrors([...errors, 'No valid FAQs found in CSV']);
        return;
      }

      const { uniqueFaqs, duplicateQuestions } = getUniqueFaqs(parsedFaqs);
      if (uniqueFaqs.length === 0) {
        setUploadStatus('error');
        setCsvErrors([...errors, 'All FAQs in this CSV are duplicates']);
        await showDuplicateAlert(duplicateQuestions, 0);
        return;
      }

      // Upload only questions that are not already present.
      const response = await axios.post(
        `${API_BASE}/manager/faq/bulk`,
        { tenant_id: tenantId, faqs: uniqueFaqs },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUploadStatus('success');
      await fetchFaqs();
      await showDuplicateAlert(
        [...duplicateQuestions, ...(response.data?.duplicate_questions || [])],
        response.data?.created ?? uniqueFaqs.length
      );
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadStatus(null);
        setCsvErrors([]);
      }, 3000);

    } catch (err) {
      console.error("CSV upload error:", err);
      setUploadStatus('error');
      setCsvErrors([err.message || 'Failed to upload CSV']);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "Question,Answer\n" +
      '"How do I reset my password?","Click on Forgot Password and follow the instructions."\n' +
      '"What is your refund policy?","Refunds are processed within 7-10 business days."';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'faq_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  /* ================= EDIT ================= */
  const startEdit = (faq) => {
    setEditingId(faq.id);
    setEditData({ question: faq.question, answer: faq.answer });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(
        `${API_BASE}/manager/faq/${id}`,
        editData,
        { params: { tenant_id: tenantId }, headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingId(null);
      fetchFaqs();
      await Swal.fire({
        icon: "success",
        title: "FAQ Updated",
        text: "The FAQ was updated successfully.",
        customClass: { popup: "swal-popup-custom" },
      });
    } catch (err) {
      console.error("Update failed:", err);
      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Failed to update FAQ. Please try again.",
        customClass: { popup: "swal-popup-custom" },
      });
    }
  };

  const toggleFaqActive = async (faq) => {
    try {
      await axios.put(
        `${API_BASE}/manager/faq/${faq.id}`,
        { active: faq.active === false },
        { params: { tenant_id: tenantId }, headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchFaqs();
    } catch (err) {
      console.error("Status update failed:", err);
      await Swal.fire({
        icon: "error",
        title: "Status Update Failed",
        text: "Failed to change FAQ status. Please try again.",
        customClass: { popup: "swal-popup-custom" },
      });
    }
  };

  /* ================= DELETE ================= */
  const deleteFaq = async (id) => {
    const confirmed = await Swal.fire({
      icon: "warning",
      title: "Delete FAQ?",
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      customClass: { popup: "swal-popup-custom" },
    });

    if (!confirmed.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/manager/faq/${id}`, {
        params: { tenant_id: tenantId },
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFaqs();
      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "FAQ deleted successfully.",
        customClass: { popup: "swal-popup-custom" },
      });
    } catch (err) {
      console.error("Delete failed:", err);
      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Failed to delete FAQ. Please try again.",
        customClass: { popup: "swal-popup-custom" },
      });
    }
  };

  /* ================= UI ================= */
  return (
    <div className="faq-manager-container">

      {/* HEADER */}
      <div className="faq-manager-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>FAQ Management</h1>
          <p>Manage frequently asked questions for your help page</p>
        </div>
        <button
          type="button"
          onClick={() => typeof onBack === 'function' ? onBack() : window.history.back()}
          style={{
            padding: '10px 16px',
            border: '1px solid #cbd5e1',
            borderRadius: 8,
            background: '#ffffff',
            color: '#1f2937',
            cursor: 'pointer',
            fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
          }}
        >
          ← Back
        </button>
      </div>

      {/* STATS ROW */}
      <div className="faq-stats-row">
        <div className="faq-stat-card">
          <div className="stat-icon stat-icon-primary">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{storedFaqs.length}</span>
            <span className="stat-label">Total FAQs</span>
          </div>
        </div>

        <div className="faq-stat-card">
          <div className="stat-icon stat-icon-success">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{storedFaqs.filter(f => f.active !== false).length}</span>
            <span className="stat-label">Active</span>
          </div>
        </div>
      </div>

      {/* CSV UPLOAD SECTION */}
      <div className="csv-upload-section">
        <div className="csv-upload-header">
          <div>
            <h3>Bulk Upload FAQs</h3>
            <p>Upload multiple FAQs at once using a CSV file</p>
          </div>
          <button className="btn-download-template" onClick={downloadTemplate}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Template
          </button>
        </div>

        <div className="csv-upload-body">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="csv-file-input"
          />
          <label htmlFor="csv-file-input" className="csv-upload-dropzone">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="upload-title">Click to upload CSV file</p>
            <p className="upload-subtitle">or drag and drop</p>
            <p className="upload-hint">CSV with Question and Answer columns</p>
          </label>

          {uploadStatus === 'success' && (
            <div className="alert alert-success">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              FAQs uploaded successfully!
            </div>
          )}

          {uploadStatus === 'error' && (
            <div className="alert alert-error">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <div>
                <strong>Upload failed</strong>
                {csvErrors.length > 0 && (
                  <ul className="error-list">
                    {csvErrors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ADD FAQS MANUALLY */}
      <div className="manual-add-section">
        <h3>Add FAQs Manually</h3>

        {newFaqs.map((f, i) => (
          <div key={i} className="faq-input-row">
            <div className="input-group">
              <label>Question {i + 1}</label>
              <input
                type="text"
                placeholder="Enter FAQ question"
                value={f.question}
                onChange={(e) => updateRow(i, "question", e.target.value)}
              />
            </div>
            <div className="input-group">
              <label>Answer {i + 1}</label>
              <textarea
                placeholder="Enter FAQ answer"
                value={f.answer}
                onChange={(e) => updateRow(i, "answer", e.target.value)}
                rows={3}
              />
            </div>
          </div>
        ))}

        <div className="manual-add-actions">
          <button className="btn-secondary" onClick={addRow}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Another Row
          </button>
          <button className="btn-primary" onClick={submitBulk} disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              <>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save All FAQs
              </>
            )}
          </button>
        </div>
      </div>

      {/* EXISTING FAQS */}
      <div className="existing-faqs-section">
        <div className="section-header">
          <h2>Existing FAQs</h2>
          <p>Default FAQs are shown if none exist. You can edit or delete them.</p>
        </div>

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading FAQs...</p>
          </div>
        )}

        <div className="faq-list">
          {currentFaqs.map((faq, index) => (
            <div
              key={faq.id}
              className={`faq-card ${openIndex === index ? "expanded" : ""}`}
            >
              <div
                className="faq-card-header"
                onClick={() => toggleAccordion(index)}
              >
                <div className="faq-number">{(currentPage - 1) * itemsPerPage + index + 1}</div>
                <h3 className="faq-question-title">{faq.question}</h3>
                <svg className={`expand-icon ${openIndex === index ? 'rotated' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {openIndex === index && (
                <div className="faq-card-body">
                  {editingId === faq.id ? (
                    <>
                      <div className="input-group">
                        <label>Question</label>
                        <input
                          value={editData.question}
                          onChange={(e) =>
                            setEditData({ ...editData, question: e.target.value })
                          }
                        />
                      </div>
                      <div className="input-group">
                        <label>Answer</label>
                        <textarea
                          value={editData.answer}
                          onChange={(e) =>
                            setEditData({ ...editData, answer: e.target.value })
                          }
                          rows={4}
                        />
                      </div>
                      <div className="faq-card-actions">
                        <button className="btn-primary" onClick={() => saveEdit(faq.id)}>
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save
                        </button>
                        <button className="btn-secondary" onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="faq-answer-text">
                        <p>{faq.answer}</p>
                      </div>
                      <div style={{ marginBottom: 12, color: faq.active === false ? '#b91c1c' : '#15803d', fontWeight: 600 }}>
                        {faq.active === false ? 'Inactive' : 'Active'}
                      </div>
                      <div className="faq-card-actions">
                        <button className="btn-edit" onClick={() => startEdit(faq)}>
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                        <button className="btn-delete" onClick={() => deleteFaq(faq.id)}>
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                        <button className="btn-secondary" onClick={() => toggleFaqActive(faq)}>
                          {faq.active === false ? 'Activate' : 'Make Inactive'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {faqs.length > itemsPerPage && (
          <div className="pagination-wrapper">
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>

            {/* {Array.from({ length: pageCount }, (_, idx) => (
              <button
                key={idx}
                className={`pagination-btn ${currentPage === idx + 1 ? "active" : ""}`}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))} */}

            <button
              className="pagination-btn"
              disabled={currentPage === pageCount}
              onClick={() => setCurrentPage((prev) => Math.min(pageCount, prev + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>

    </div>
  );
};

export default ManagerFAQManagement;